import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'src/makeStyles';
import MuiButton from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import isEmpty from 'lodash/isEmpty';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ToggleSwitch from 'components/ToggleSwitch';
import { Grid } from '@mui/material';
import ButtonV1 from 'components/Button/ButtonV1';
import { usePropsDrillProvider } from 'utils/hooks/usePropsDrillProvider';
import usePermissions from 'utils/permissions';

import { useTranslation } from '../../../src/i18n';
import {
  TP,
  LOADER_VARIANTS,
  MEDIA_TYPES_IDS,
  BUTTON_VARIANTS,
  MEDIA_STATUS,
  IN_VALID_ACTION_TYPES,
} from '../../../constants/consts';
import CustomChip from '../../CustomChips/CustomChip';
import DeleteButton from '../../Button/DeleteButton';
import EditButton from '../../Button/EditButton';
import FbCircularProgress from '../../FbCircularProgress';
import ImageTransformation from '../../Transformation/images';
import ConfirmDeleteModal from '../../EditComponents/ConfirmDeleteModal';

import { colors, fontSize, fontFamilies } from '../../../constants/styleConstants';
const useStyles = makeStyles()((theme, props) => ({
  root: {
    width: props.width + 2,
    height: 'auto',
    border: `1px solid ${colors.background.lightGrey}`,
    borderRadius: 4,
    overflow: 'hidden',
    position: 'relative',
    '&.isNew': {
      borderColor: colors.blue3,
    },
  },
  error: {
    border: `1px solid ${colors.red5}`,
  },
  mediaPlaceholder: {
    height: props.height + 2,
    background: colors.grey9,
    justifyContent: 'center',
  },
  mediaPlaceholderIcon: {
    color: colors.grey2,
  },
  content: {
    background: colors.white,
    height: props.isVideoType ? 130 : 100,
    padding: '10px 12px',
    '&.hideContentTitle': {
      height: 'auto',
    },
    '&.noPadding': {
      padding: 0,
    },
    '&.isLoading': {
      pointerEvents: 'none',
      opacity: 0.3,
    },
  },
  addContentWrapper: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaWrapper: {
    position: 'relative',
    cursor: 'pointer',
    height: '100%',
    width: '100%',
    '& > div': {
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.grey12,
    },
    '& img': {
      height: '100%',
      width: '100%',
      objectFit: 'contain',
    },
    '& .MuiSvgIcon-root': {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      margin: 'auto',
      height: 24,
      width: 24,
      color: colors.white,
    },
  },
  mediaContent: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  mediaTitle: {
    color: colors.grey3,
    fontSize: 16,
    lineHeight: '19px !important',
    height: props.isVideoType ? 58 : 25,
    width: '100%',
    overflow: 'hidden',
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mediaActions: {
    margin: '0 -5px',
    width: 'calc(100% + 10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  mediaVisibility: {
    flex: 2,
  },
  mediaUpdate: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& button + button': {
      marginLeft: 10,
    },
  },
  addButton: {
    padding: 0,
    fontSize: 16,
    fontFamily: 'RobotoBold',
    lineHeight: '19px',
    borderRadius: 4,
    textTransform: 'uppercase',
    minWidth: 0,
    color: colors.blue3,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:active': {
      color: colors.blue6,
      backgroundColor: 'transparent',
    },
  },
  mediaTag: {
    position: 'absolute',
    top: 8,
    left: 8,
    fontFamily: fontFamilies.ROBOTO_BOLD,
    fontSize: fontSize.ten,
    lineHeight: '14px',
    textTransform: 'uppercase',
    borderRadius: 4,
    padding: '2px 4px',
    display: 'inline-flex',
    alignItems: 'center',
  },
  newTag: {
    background: '#DAE3FA',
    fontFamily: fontFamilies.ROBOTO,
    fontSize: fontSize.smallTitle,
    color: '#1F52BB',
    lineHeight: '12px',
    borderRadius: 8,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    fontStyle: 'italic',
    height: 20,
    marginLeft: 8,
  },
  verificationTag: {
    border: `1px solid ${colors.orangeRed}`,
    background: colors.red2,
    color: colors.orangeRed,
    fontFamily: fontFamilies.ROBOTO_BOLD,
    fontSize: fontSize.ten,
    lineHeight: '14px',
    borderRadius: 4,
    alignItems: 'center',
    padding: '8px 4px',
    textTransform: 'uppercase',
  },
  hiddenTag: {
    textTransform: 'capitalize',
  },
  showHideBtn: {
    padding: 10,
    float: 'right',
  },
  bluredContainer: {
    filter: 'blur(6px)',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  visibilityIcon: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
  reviewNowBtn: {
    position: 'absolute',
    top: 'calc(50% - 10px)',
    left: '20%',
    right: '20%',
    zIndex: 1,
  },
}));

const MediaCard = ({
  className,
  src,
  title,
  alt,
  status,
  type,
  primaryTagLabel,
  transformation,
  lazyload,
  isPrimary,
  isLoading,
  cloudinary,
  onAdd,
  onEdit,
  onDelete,
  onMediaClick,
  disabled,
  allowMediaAddition,
  addLabel,
  hideTitle,
  isNew,
  isVerifying,
  disableEdit,
  disableDelete,
  disabledActionTooltip,
  visibilityAction,
  showHiddenTag,
  isError,
}) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const { impersonationAccount, setInValidAction } = usePropsDrillProvider();
  const isVideoType = type === MEDIA_TYPES_IDS.VIDEO;
  const { classes, cx } = useStyles({ isVideoType, width: transformation?.width, height: transformation?.height });
  const { t } = useTranslation();
  const { isAdmin } = usePermissions();

  const onShow = useCallback(() => {
    if (visibilityAction?.isHiddenProduction) {
      setInValidAction(IN_VALID_ACTION_TYPES.HIDDEN_PRODUCTION);
      return;
    }

    if (visibilityAction?.onChange) {
      visibilityAction.onChange(true);
    }
  }, []);

  const onHide = useCallback(() => {
    if (visibilityAction?.onChange) {
      visibilityAction.onChange(false);
    }
  }, []);

  const onShowHandler = impersonationAccount(onShow);
  const onHideHandler = impersonationAccount(onHide);

  const getImage = () => {
    switch (status) {
      case MEDIA_STATUS.COMPLETED:
        return (
          <ImageTransformation
            transformation={transformation}
            src={src}
            alt={alt}
            lazyload={lazyload}
            cloudinary={cloudinary}
          />
        );
      case MEDIA_STATUS.ERROR:
        return <div className={classes.imageNotAvailable}>{t(`${TP}.FN_ERROR_PROCESSING_MEDIA`)}</div>;
      case MEDIA_STATUS.NOT_AVAILABLE:
        return <div className={classes.imageNotAvailable}>{t(`${TP}.FN_MEDIA_NOT_AVAILABLE`)}</div>;
      case MEDIA_STATUS.PROCESSING:
        return <div className={classes.imageNotAvailable}>{t(`${TP}.FN_MEDIA_UPLOAD_PROCESSING`)}</div>;
      default:
        return <div className={classes.imageNotAvailable}>{t(`${TP}.FN_MEDIA_NOT_AVAILABLE`)}</div>;
    }
  };

  const getAddMediaLabel = mediaType => {
    if (mediaType === MEDIA_TYPES_IDS.IMAGE) {
      return t(`${TP}.FN_ADD_IMAGE`);
    }

    if (mediaType === MEDIA_TYPES_IDS.VIDEO) {
      return t(`${TP}.FN_ADD_VIDEO`);
    }

    return t(`${TP}.m_ADD`);
  };

  const handleReviewNow = () => {
    if (visibilityAction?.isVisible) {
      onShowHandler();
    } else {
      onHideHandler();
    }
  };

  const showMediaActions = onEdit || onDelete || visibilityAction?.onChange;
  const isAcknowledged = visibilityAction?.isAcknowledged;
  const skipToggle = visibilityAction?.skipToggle || false;
  const isVisible = visibilityAction?.isVisible;
  const showPrimaryTag = isPrimary && !isEmpty(primaryTagLabel);

  return (
    <div className={cx(classes.root, className, { isNew }, isError && classes.error)}>
      {!skipToggle && (
        <Grid
          container
          direction="row-reverse"
          justifyContent="space-between"
          alignItems="center"
          height={50}
          padding={1}
        >
          {(isAcknowledged || isAdmin) && (
            <Grid item>
              <ToggleSwitch
                isAcknowledged={visibilityAction?.isAcknowledged}
                isVisible={visibilityAction?.isVisible}
                onChangeVisibility={visibilityAction?.onChange}
                isUpdating={isLoading}
                inValidAction={visibilityAction?.inValidAction}
                isHiddenProduction={visibilityAction?.isHiddenProduction}
                type={IN_VALID_ACTION_TYPES.VALIDATION_PENDING_MEDIA}
              />
            </Grid>
          )}
          <Grid item>{!isAcknowledged && !isAdmin && (isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />)}</Grid>
          {isNew && !isAdmin && (
            <Grid item className={classes.newTag}>
              {t(`${TP}.FN_NEW_TAG`)}
            </Grid>
          )}
          {isVerifying && !isNew && (
            <Grid item className={cx(classes.verificationTag)}>
              {t(`${TP}.FN_VERIFICATION_PROGRESS`)}
            </Grid>
          )}
          <Grid item className={classes.reviewNowBtn}>
            {!isAcknowledged && !isAdmin && (
              <ButtonV1 isLoading={isLoading} onClick={() => handleReviewNow()}>
                {t(`${TP}.FN_REVIEW_NOW`)}
              </ButtonV1>
            )}
          </Grid>
        </Grid>
      )}
      <Grid item container className={cx('', { [classes.bluredContainer]: !isAcknowledged && !isAdmin })}>
        <Grid item container className={classes.mediaPlaceholder}>
          {allowMediaAddition ? (
            <Grid item className={classes.addContentWrapper} justifyContent="center">
              <div className={classes.mediaPlaceholderIcon}>{isVideoType ? <MovieIcon /> : <ImageIcon />}</div>
            </Grid>
          ) : (
            <Grid item justifyContent="center" className={classes.mediaWrapper} onClick={onMediaClick}>
              {isLoading ? <FbCircularProgress variant={LOADER_VARIANTS.PRIMARY} /> : getImage()}
              {!isLoading && isVideoType && status === MEDIA_STATUS.COMPLETED && <PlayCircleFilledWhiteIcon />}
            </Grid>
          )}
        </Grid>
        <Grid
          container
          item
          className={cx(classes.content, {
            hideContentTitle: hideTitle,
            noPadding: !showMediaActions && !showPrimaryTag && hideTitle,
            isLoading,
          })}
        >
          {allowMediaAddition ? (
            <Grid item xs={12} className={classes.addContentWrapper}>
              <MuiButton className={classes.addButton} variant="text" onClick={onAdd}>
                {addLabel || getAddMediaLabel(type)}
              </MuiButton>
            </Grid>
          ) : (
            <Grid item container className={classes.mediaContent}>
              {!hideTitle && (
                <Grid item className={cx(classes.mediaTitle, { [classes.ellipsis]: type === MEDIA_TYPES_IDS.IMAGE })}>
                  {title}
                </Grid>
              )}
              <Grid item className={classes.mediaPrimary}>
                {showPrimaryTag && <CustomChip size="small" label={primaryTagLabel} editable={false} />}
                {showHiddenTag && (
                  <CustomChip size="small" label={t(`${TP}.HIDDEN`)} className={classes.hiddenTag} editable={false} />
                )}
              </Grid>
              {showMediaActions && (
                <Grid item container direction="row-reverse" spacing={1}>
                  {(!visibilityAction || isAcknowledged) && (
                    <>
                      <Grid item>
                        {onEdit && !disableEdit && (
                          <Tooltip title={disabledActionTooltip && disabled ? disabledActionTooltip : ''}>
                            <span>
                              <EditButton disabled={disabled} onClick={() => onEdit()} />
                            </span>
                          </Tooltip>
                        )}
                      </Grid>
                      <Grid item>
                        {onDelete && !disableDelete && (
                          <Tooltip title={disabledActionTooltip && disabled ? disabledActionTooltip : ''}>
                            <span>
                              <DeleteButton disabled={disabled} onClick={() => setShowConfirmDelete(true)} />
                            </span>
                          </Tooltip>
                        )}
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
      {showConfirmDelete && (
        <ConfirmDeleteModal
          closeModal={() => setShowConfirmDelete(false)}
          title={
            isVideoType
              ? t(`${TP}.FN_DELETE_ARTIST_VIDEO_CONFIRMATION`)
              : t(`${TP}.FN_DELETE_ARTIST_IMAGE_CONFIRMATION`)
          }
          onDelete={() => {
            setShowConfirmDelete(false);
            onDelete();
          }}
          buttonVariant={BUTTON_VARIANTS.PRIMARY}
        />
      )}
    </div>
  );
};

MediaCard.propTypes = {
  className: PropTypes.string,
  src: PropTypes.string,
  title: PropTypes.string,
  alt: PropTypes.string,
  type: PropTypes.number,
  primaryTagLabel: PropTypes.string,
  transformation: PropTypes.object,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  isPrimary: PropTypes.bool,
  isLoading: PropTypes.bool,
  lazyload: PropTypes.bool,
  onMediaClick: PropTypes.func,
  cloudinary: PropTypes.bool,
  allowMediaAddition: PropTypes.bool,
  addLabel: PropTypes.string,
  hideTitle: PropTypes.bool,
  isNew: PropTypes.bool,
  isVerifying: PropTypes.bool,
  visibilityAction: PropTypes.shape({
    isAcknowledged: PropTypes.bool,
    isVisible: PropTypes.bool,
    onChange: PropTypes.func,
  }),
  disableDelete: PropTypes.bool,
  disableEdit: PropTypes.bool,
  showHiddenTag: PropTypes.bool,
  disabledActionTooltip: PropTypes.string,
  isError: PropTypes.bool,
};

MediaCard.defaultProps = {
  type: MEDIA_TYPES_IDS.VIDEO,
  primaryTagLabel: '',
  title: '',
  alt: '',
  onAdd: () => {},
  onMediaClick: () => {},
  isPrimary: false,
  isLoading: false,
  lazyload: false,
  cloudinary: true,
  allowMediaAddition: false,
  isNew: false,
  isVerifying: false,
  disableDelete: false,
  disableEdit: false,
  showHiddenTag: false,
  isError: false,
};

export default MediaCard;
